import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { useUser } from '../context/UserContext';
import {
  moraAnularSolicitud,
  moraCursarSolicitud,
  moraListarSolicitudes,
  moraReemplazosCancelar,
  moraReemplazosCrear,
  moraReemplazosListar,
  moraReporteMargen,
  moraResolverSolicitud,
  moraPlazoPagoObtener,
  moraPlazoPagoGuardar,
  moraPizarraPagos,
  moraReenviarAprobacion,
  moraPreacordadasPendientes,
  moraAplicarPreacordada,
} from '../api/apiConfig';
import { canAccessConfigPlazoMoraPagos, isAplicacionesUser } from '../utils/gestionCampanaAccess';
import './SimuladorMoraExcepcion.css';
import './SolicitudesExcepcionMora.css';
import './TasasMoraPreacordadas.css';

const CLP = new Intl.NumberFormat('es-CL', {
  style: 'currency',
  currency: 'CLP',
  maximumFractionDigits: 0,
});
const PCT = new Intl.NumberFormat('es-CL', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const PCT_TMC = new Intl.NumberFormat('es-CL', {
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
});

const CHILE_TZ = 'America/Santiago';

/** Datetimes de la lambda llegan en UTC (naive o con Z); se muestran en hora Chile. */
function formatDateTime(value) {
  if (!value) return '—';
  let s = String(value).trim();
  if (!s) return '—';
  if (!/(?:Z|[+-]\d{2}:?\d{2})$/i.test(s)) {
    s = s.includes('T') ? `${s}Z` : `${s.replace(' ', 'T')}Z`;
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('es-CL', { timeZone: CHILE_TZ });
}

function formatDate(value) {
  if (!value) return '—';
  const s = String(value).slice(0, 10);
  const [y, m, d] = s.split('-').map(Number);
  if (!y) return String(value);
  return new Date(y, m - 1, d).toLocaleDateString('es-CL');
}

function formatRutCli(row) {
  if (!row) return '—';
  if (row.rut_cli) return row.rut_cli;
  if (row.rut != null && row.dv) return `${row.rut}-${String(row.dv).toUpperCase()}`;
  return '—';
}

function nivelLabel(nivel, origen) {
  const o = String(origen || '').toUpperCase();
  if (o === 'PREACORDADA') {
    return 'Preacordada → Aplicaciones';
  }
  if (o === 'TMC') {
    return 'TMC — sin excepción';
  }
  return nivel === 'LIDER_EQUIPO' ? 'Líder de equipo' : 'Gerencia';
}

function origenLabel(origen) {
  const o = String(origen || 'MANUAL').toUpperCase();
  if (o === 'PREACORDADA') return 'Preacordada';
  if (o === 'TMC') return 'TMC';
  return 'Manual';
}

function origenBadgeClass(origen) {
  const o = String(origen || 'MANUAL').toUpperCase();
  if (o === 'PREACORDADA') return 'aprobada';
  if (o === 'TMC') return 'tmc';
  return 'pendiente';
}

function cursadoLabel(estadoCursado) {
  const v = String(estadoCursado || 'PENDIENTE').toUpperCase();
  if (v === 'OK') return 'OK';
  if (v === 'NO') return 'NO';
  return 'Pendiente';
}

/** Etiqueta de operaciones (docs o campo agregado del API). */
function operacionLabel(detalleOrOps) {
  if (typeof detalleOrOps === 'string' && detalleOrOps.trim()) {
    return detalleOrOps.trim();
  }
  if (!Array.isArray(detalleOrOps) || !detalleOrOps.length) {
    if (detalleOrOps?.num_opes_label) return String(detalleOrOps.num_opes_label);
    if (detalleOrOps?.operaciones) {
      const parts = String(detalleOrOps.operaciones)
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean);
      if (parts.length === 1) return `Op. ${parts[0]}`;
      if (parts.length <= 3) return `Ops. ${parts.join(', ')}`;
      if (parts.length > 3) return `${parts.length} operaciones`;
    }
    return '—';
  }
  const ops = [
    ...new Set(
      detalleOrOps
        .map((d) => d.num_ope ?? d.NUM_OPE ?? d.operationNumber)
        .filter((v) => v != null && String(v).trim() !== '')
        .map((v) => String(v).trim())
    ),
  ];
  if (!ops.length) return '—';
  if (ops.length === 1) return `Op. ${ops[0]}`;
  if (ops.length <= 3) return `Ops. ${ops.join(', ')}`;
  return `${ops.length} operaciones`;
}

/** Números de documento asociados (máx. 5; si hay más, +N). */
function documentosLabel(rowOrDetalle, maxShow = 5) {
  if (rowOrDetalle?.num_doctos_label) return String(rowOrDetalle.num_doctos_label);
  let parts = [];
  if (Array.isArray(rowOrDetalle)) {
    parts = [
      ...new Set(
        rowOrDetalle
          .map((d) => d.num_docto ?? d.NUM_DOCTO ?? d.documentNumber)
          .filter((v) => v != null && String(v).trim() !== '')
          .map((v) => String(v).trim())
      ),
    ];
  } else if (rowOrDetalle?.documentos) {
    parts = String(rowOrDetalle.documentos)
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);
  }
  if (!parts.length) return '—';
  if (parts.length <= maxShow) return parts.join(', ');
  return `${parts.slice(0, maxShow).join(', ')} +${parts.length - maxShow}`;
}

function deudorKey(d) {
  const rut = String(d?.rut_deudor_cli || '').trim().toUpperCase();
  if (rut) return rut;
  const nom = String(d?.deudor_nombre || '').trim();
  return nom ? `N:${nom.toUpperCase()}` : '__SIN__';
}

function deudorMeta(d) {
  const nombre = String(d?.deudor_nombre || '').trim();
  const rut = String(d?.rut_deudor_cli || '').trim();
  if (nombre || rut) {
    return { nombre: nombre || 'Deudor', rut };
  }
  return { nombre: 'Sin deudor', rut: '' };
}

function groupRowsByDeudor(list) {
  const map = new Map();
  for (const r of list || []) {
    const k = deudorKey(r);
    if (!map.has(k)) {
      map.set(k, { key: k, ...deudorMeta(r), rows: [] });
    }
    map.get(k).rows.push(r);
  }
  return [...map.values()].sort((a, b) => {
    if (a.key === '__SIN__') return 1;
    if (b.key === '__SIN__') return -1;
    return a.nombre.localeCompare(b.nombre, 'es');
  });
}

/** Quién debe / quien resolvió la solicitud. */
function aprobadorLabel(s) {
  if (!s) return '—';
  const o = String(s.origen || '').toUpperCase();
  if (o === 'PREACORDADA') {
    return s.resuelto_por_nombre || 'Sistema — tasa preacordada';
  }
  if (o === 'TMC') {
    return s.resuelto_por_nombre || s.ejecutivo_nombre || 'Ejecutivo';
  }
  if (s.estado !== 'PENDIENTE' && (s.resuelto_por_nombre || s.resuelto_por_mail)) {
    return s.resuelto_por_nombre || s.resuelto_por_mail;
  }
  if (s.nivel_aprobacion === 'LIDER_EQUIPO') {
    return s.ejecutivo_grupo
      ? `Líder de equipo (${s.ejecutivo_grupo})`
      : 'Líder de equipo';
  }
  return 'Gerencia';
}

function monthStartISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function todayFileStamp() {
  return new Date().toISOString().split('T')[0];
}

function downloadExcelWorkbook(sheets, fileName) {
  const wb = XLSX.utils.book_new();
  let added = 0;
  for (const { name, rows } of sheets) {
    if (!Array.isArray(rows) || !rows.length) continue;
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, String(name || 'Hoja').slice(0, 31));
    added += 1;
  }
  if (!added) return false;
  XLSX.writeFile(wb, fileName);
  return true;
}

const SolicitudesExcepcionMora = () => {
  const { user } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const codigoParam = searchParams.get('codigo') || '';

  const [tab, setTab] = useState('bandeja');
  const [filtroEstado, setFiltroEstado] = useState('PENDIENTE');
  const [filtroCursado, setFiltroCursado] = useState('PENDIENTE');
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detalle, setDetalle] = useState([]);
  const [comentario, setComentario] = useState('');
  const [resolving, setResolving] = useState(false);
  const [toast, setToast] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [puedeAnular, setPuedeAnular] = useState(false);
  const [puedeCursar, setPuedeCursar] = useState(false);

  const esAplicaciones = isAplicacionesUser(user);

  useEffect(() => {
    if (esAplicaciones) {
      setFiltroEstado('APROBADA');
      setFiltroCursado('PENDIENTE');
      setTab('bandeja');
    }
  }, [esAplicaciones]);

  const [fechaDesde, setFechaDesde] = useState(monthStartISO());
  const [fechaHasta, setFechaHasta] = useState(todayISO());
  const [reporte, setReporte] = useState(null);
  const [loadingReporte, setLoadingReporte] = useState(false);

  const [reemplazos, setReemplazos] = useState([]);
  const [candidatos, setCandidatos] = useState([]);
  const [meReemplazan, setMeReemplazan] = useState([]);
  const [loadingReemplazos, setLoadingReemplazos] = useState(false);
  const [savingReemplazo, setSavingReemplazo] = useState(false);
  const [repMail, setRepMail] = useState('');
  const [repDesde, setRepDesde] = useState(todayISO());
  const [repHasta, setRepHasta] = useState(todayISO());
  const [reemplazosVigentesBandeja, setReemplazosVigentesBandeja] = useState([]);

  const [plazoModalOpen, setPlazoModalOpen] = useState(false);
  const [plazoDias, setPlazoDias] = useState(0);
  const [plazoDraft, setPlazoDraft] = useState('0');
  const [plazoMeta, setPlazoMeta] = useState({ actualizado_por: null, actualizado_en: null });
  const [loadingPlazo, setLoadingPlazo] = useState(false);
  const [savingPlazo, setSavingPlazo] = useState(false);

  const [pizarraModalOpen, setPizarraModalOpen] = useState(false);
  const [pizarraFilas, setPizarraFilas] = useState([]);
  const [pizarraFechaCarga, setPizarraFechaCarga] = useState(null);
  const [pizarraTotal, setPizarraTotal] = useState(0);
  const [pizarraFiltro, setPizarraFiltro] = useState('');
  const [loadingPizarra, setLoadingPizarra] = useState(false);

  const [preacordadasPendientes, setPreacordadasPendientes] = useState([]);
  const [loadingPreacordadas, setLoadingPreacordadas] = useState(false);
  const [aplicandoKey, setAplicandoKey] = useState('');
  const [preacordadasMeta, setPreacordadasMeta] = useState({
    n_documentos: 0,
    n_clientes: 0,
    plazo_dias: null,
  });

  const esAdmin = String(user?.tipo_usuario || '') === 'Administrador';
  const puedeVerPizarra = esAplicaciones || esAdmin;
  const puedeVerPreacordadasPendientes = esAplicaciones || esAdmin;

  const puedeGestionarReemplazos = ['Gerencia', 'Lider de equipo', 'Administrador'].includes(
    String(user?.tipo_usuario || '')
  );
  const puedeCrearReemplazo = ['Gerencia', 'Lider de equipo'].includes(
    String(user?.tipo_usuario || '')
  );
  const puedeConfigPlazo = canAccessConfigPlazoMoraPagos(user);

  const showToast = useCallback((msg) => {
    setToast(msg);
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(''), 3200);
  }, []);

  const loadBandeja = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const payload = {
        estado: filtroEstado || undefined,
      };
      if (esAplicaciones && filtroEstado === 'PENDIENTE') {
        // Solo consulta de pedidos aún no aprobados; no aplica filtro de cursado.
      } else if (esAplicaciones) {
        // Default PENDIENTE; value '' = ver todas (OK/NO/pendientes).
        payload.estado_cursado = filtroCursado === '' ? 'ALL' : filtroCursado || 'PENDIENTE';
      } else if (filtroEstado === 'APROBADA' || !filtroEstado) {
        if (filtroCursado) payload.estado_cursado = filtroCursado;
      }
      const res = await moraListarSolicitudes(user, payload);
      setSolicitudes(Array.isArray(res?.solicitudes) ? res.solicitudes : []);
      setPuedeAnular(Boolean(res?.puede_anular));
      setPuedeCursar(Boolean(res?.puede_cursar) || esAplicaciones);
      setReemplazosVigentesBandeja(
        Array.isArray(res?.reemplazos_activos) ? res.reemplazos_activos : []
      );
    } catch (e) {
      console.error(e);
      showToast(e.message || 'Error al cargar solicitudes');
      setSolicitudes([]);
      setPuedeAnular(false);
      setPuedeCursar(false);
      setReemplazosVigentesBandeja([]);
    } finally {
      setLoading(false);
    }
  }, [user, filtroEstado, filtroCursado, esAplicaciones, showToast]);

  const loadPreacordadasPendientes = useCallback(async () => {
    if (!user || !puedeVerPreacordadasPendientes) return;
    setLoadingPreacordadas(true);
    try {
      const res = await moraPreacordadasPendientes(user, {});
      setPreacordadasPendientes(Array.isArray(res?.items) ? res.items : []);
      setPreacordadasMeta({
        n_documentos: Number(res?.n_documentos || 0),
        n_clientes: Number(res?.n_clientes || 0),
        plazo_dias: res?.plazo_dias != null ? Number(res.plazo_dias) : null,
      });
    } catch (e) {
      console.error(e);
      showToast(e.message || 'Error al cargar preacordadas pendientes');
      setPreacordadasPendientes([]);
      setPreacordadasMeta({ n_documentos: 0, n_clientes: 0, plazo_dias: null });
    } finally {
      setLoadingPreacordadas(false);
    }
  }, [user, puedeVerPreacordadasPendientes, showToast]);

  useEffect(() => {
    if (tab === 'bandeja') loadBandeja();
  }, [tab, loadBandeja]);

  useEffect(() => {
    if (tab === 'preacordadas' || puedeVerPreacordadasPendientes) {
      // Contador del tab también al entrar a la vista Aplicaciones/Admin.
      if (tab === 'preacordadas' || esAplicaciones) loadPreacordadasPendientes();
    }
  }, [
    tab,
    loadPreacordadasPendientes,
    puedeVerPreacordadasPendientes,
    esAplicaciones,
  ]);

  const aplicarPreacordadaCliente = useCallback(
    async (item, docsSubset = null, opts = {}) => {
      if (!user || !item?.rut_cli || !Array.isArray(item.documentos) || !item.documentos.length) {
        return;
      }
      const source = Array.isArray(docsSubset) ? docsSubset : item.documentos;
      const docsOk = source.filter((d) => d.tasa_preacordada != null);
      if (!docsOk.length) {
        showToast('No hay documentos con tasa preacordada resoluble para aplicar');
        return;
      }
      const label = opts.label || item.cliente_nombre || item.rut_cli;
      const ok = window.confirm(
        `¿Aplicar tasa preacordada a ${docsOk.length} documento(s) de ${label}?\n\nSe creará una solicitud APROBADA y quedará pendiente de cursar.`
      );
      if (!ok) return;
      const applyKey = opts.applyKey || String(item.rut);
      setAplicandoKey(applyKey);
      try {
        const res = await moraAplicarPreacordada(user, {
          rut_cli: item.rut_cli,
          cliente_nombre: item.cliente_nombre,
          documentos: docsOk.map((d) =>
            d.id != null
              ? { id: d.id, num_docto: d.num_docto }
              : { num_docto: d.num_docto }
          ),
          motivo:
            'Aplicación de tasa de mora preacordada según política comercial vigente.',
        });
        showToast(res?.mensaje || `Solicitud ${res?.codigo || ''} creada`);
        await loadPreacordadasPendientes();
        if (esAplicaciones) {
          setFiltroCursado('PENDIENTE');
          setTab('bandeja');
        }
      } catch (e) {
        console.error(e);
        showToast(e.message || 'No se pudo aplicar la tasa preacordada');
      } finally {
        setAplicandoKey('');
      }
    },
    [user, showToast, loadPreacordadasPendientes, esAplicaciones]
  );

  const openDetalle = useCallback(
    async (codigo) => {
      if (!user || !codigo) return;
      try {
        const res = await moraListarSolicitudes(user, { codigo });
        const sol = (res?.solicitudes || [])[0];
        if (!sol) {
          showToast('Solicitud no encontrada o sin acceso');
          return;
        }
        setSelected(sol);
        setDetalle(Array.isArray(res?.detalle) ? res.detalle : []);
        setComentario('');
        setModalOpen(true);
        if (typeof res?.puede_anular === 'boolean') {
          setPuedeAnular(res.puede_anular);
        }
        if (typeof res?.puede_cursar === 'boolean') {
          setPuedeCursar(res.puede_cursar);
        }
      } catch (e) {
        console.error(e);
        showToast(e.message || 'Error al abrir solicitud');
      }
    },
    [user, showToast]
  );

  useEffect(() => {
    if (codigoParam) {
      setTab('bandeja');
      openDetalle(codigoParam);
    }
  }, [codigoParam, openDetalle]);

  const closeModal = () => {
    setModalOpen(false);
    setSelected(null);
    setDetalle([]);
    if (codigoParam) {
      const next = new URLSearchParams(searchParams);
      next.delete('codigo');
      setSearchParams(next, { replace: true });
    }
  };

  const handleResolver = async (decision) => {
    if (!selected) return;
    setResolving(true);
    try {
      const res = await moraResolverSolicitud(user, {
        codigo: selected.codigo,
        decision,
        comentario: comentario.trim() || undefined,
      });
      const mailOk = Number(res?.email_resolucion?.sent || 0) > 0;
      showToast(
        res?.mensaje ||
          `Solicitud ${selected.codigo} marcada como ${decision}${
            mailOk ? '. Ejecutivo notificado.' : '.'
          }`
      );
      closeModal();
      loadBandeja();
    } catch (e) {
      console.error(e);
      showToast(e.message || 'No se pudo resolver');
    } finally {
      setResolving(false);
    }
  };

  const handleReenviarAprobacion = async () => {
    if (!selected || selected.estado !== 'PENDIENTE' || !esAdmin) return;
    const ok = window.confirm(
      `¿Reenviar el correo de aprobación de ${selected.codigo}?\n\n` +
        'Se notificará de nuevo al aprobador (y copia a Gerencia).'
    );
    if (!ok) return;
    setResolving(true);
    try {
      const res = await moraReenviarAprobacion(user, { codigo: selected.codigo });
      const mailOk = Number(res?.email?.sent || 0) > 0;
      showToast(
        res?.mensaje ||
          (mailOk
            ? `Correo reenviado para ${selected.codigo}`
            : `No se pudo reenviar ${selected.codigo}`)
      );
    } catch (e) {
      console.error(e);
      showToast(e.message || 'No se pudo reenviar el correo');
    } finally {
      setResolving(false);
    }
  };

  const handleAnular = async () => {
    if (!selected || selected.estado !== 'APROBADA') return;
    const ok = window.confirm(
      `¿Anular la aprobación de ${selected.codigo}?\n\n` +
        'Los documentos volverán a estar disponibles para solicitar excepción. ' +
        'La solicitud dejará de contar en el análisis de margen. ' +
        'Se notificará a Aplicaciones.'
    );
    if (!ok) return;
    setResolving(true);
    try {
      const res = await moraAnularSolicitud(user, {
        codigo: selected.codigo,
        comentario: comentario.trim() || undefined,
      });
      const mailOk = Number(res?.email_operaciones?.sent || 0) > 0;
      showToast(
        res?.mensaje ||
          `Solicitud ${selected.codigo} anulada${
            mailOk ? '. Aplicaciones notificada.' : '.'
          }`
      );
      closeModal();
      loadBandeja();
    } catch (e) {
      console.error(e);
      showToast(e.message || 'No se pudo anular la aprobación');
    } finally {
      setResolving(false);
    }
  };

  const handleCursar = async (estadoCursado) => {
    if (!selected || selected.estado !== 'APROBADA') return;
    setResolving(true);
    try {
      const res = await moraCursarSolicitud(user, {
        codigo: selected.codigo,
        estado_cursado: estadoCursado,
        comentario: comentario.trim() || undefined,
      });
      showToast(res?.mensaje || `Cursado marcado como ${estadoCursado}`);
      closeModal();
      // Volver a pendientes: al cambiar el filtro se recarga; si ya estaba, refrescar ya.
      if (esAplicaciones && filtroCursado !== 'PENDIENTE') {
        setFiltroCursado('PENDIENTE');
      } else {
        await loadBandeja();
      }
    } catch (e) {
      console.error(e);
      showToast(e.message || 'No se pudo actualizar el cursado');
    } finally {
      setResolving(false);
    }
  };

  const exportBandejaExcel = () => {
    if (!solicitudes.length) {
      showToast('No hay filas para exportar');
      return;
    }
    const rows = solicitudes.map((s) => {
      const base = {
        Código: s.codigo,
        Cliente: s.cliente_nombre,
        RUT: formatRutCli(s),
        ...(esAplicaciones
          ? { Documentos: documentosLabel(s), Deudores: s.deudores_label || '' }
          : { Operación: operacionLabel(s) }),
        Ejecutivo: s.ejecutivo_nombre,
        Grupo: s.ejecutivo_grupo || '',
        'Tasa solicitada (%)': Number(s.tasa_mensual_solicitada),
        'TMC mensual (%)': Number(s.tmc_mensual),
      };
      if (!esAplicaciones) {
        base['Margen perdido'] = Number(s.margen_perdido || 0);
      }
      return {
        ...base,
        'Deuda neta': Number(s.deuda_neta_seleccionada || 0),
        'Mora TMC': Number(s.mora_tmc || 0),
        'Mora solicitada': Number(s.mora_solicitada || 0),
        Origen: origenLabel(s.origen),
        Nivel: nivelLabel(s.nivel_aprobacion, s.origen),
        Aprobador: aprobadorLabel(s),
        Estado: s.estado,
        Cursado: s.estado === 'APROBADA' ? cursadoLabel(s.estado_cursado) : '',
        'Fecha solicitud': formatDateTime(s.fecha_solicitud),
        'Fecha pago est.': s.fecha_pago_estimada,
        Motivo: s.motivo || '',
      };
    });
    const ok = downloadExcelWorkbook(
      [{ name: 'Bandeja', rows }],
      `excepcion_mora_bandeja_${todayFileStamp()}.xlsx`
    );
    showToast(ok ? 'Excel descargado' : 'No se pudo exportar');
  };

  const exportReporteExcel = () => {
    if (!reporte) {
      showToast('Calcula el reporte antes de exportar');
      return;
    }
    const res = reporte.resumen || {};
    const porEjec = reporte.por_ejecutivo || [];
    const porM = reporte.por_mes || [];
    const det = reporte.detalle || [];
    const sheets = [
      {
        name: 'Resumen',
        rows: [
          {
            'Fecha desde': fechaDesde,
            'Fecha hasta': fechaHasta,
            'Impacto margen': Number(res.total_margen_perdido || 0),
            'Mora TMC': Number(res.total_mora_tmc || 0),
            'Mora especial': Number(res.total_mora_solicitada || 0),
            'Deuda neta': Number(res.total_deuda_neta || 0),
            Solicitudes: Number(res.n_solicitudes || 0),
          },
        ],
      },
      {
        name: 'Por ejecutivo',
        rows: porEjec.map((r) => ({
          Ejecutivo: r.ejecutivo_nombre,
          Grupo: r.ejecutivo_grupo || '',
          Solicitudes: Number(r.n_solicitudes || 0),
          'Margen perdido': Number(r.margen_perdido || 0),
          'Deuda neta': Number(r.deuda_neta || 0),
          'Tasa promedio (%)': Number(r.tasa_promedio || 0),
        })),
      },
      {
        name: 'Por mes',
        rows: porM.map((r) => ({
          Año: r.anio,
          Mes: r.mes,
          Solicitudes: Number(r.n_solicitudes || 0),
          'Margen perdido': Number(r.margen_perdido || 0),
        })),
      },
      {
        name: 'Detalle',
        rows: det.map((r) => ({
          Código: r.codigo,
          Cliente: r.cliente_nombre,
          Operación: operacionLabel(r),
          Ejecutivo: r.ejecutivo_nombre,
          Grupo: r.ejecutivo_grupo || '',
          'Fecha solicitud': r.fecha_solicitud,
          'TMC mensual (%)': Number(r.tmc_mensual),
          'Tasa (%)': Number(r.tasa_mensual_solicitada),
          'Margen perdido': Number(r.margen_perdido || 0),
        })),
      },
    ];
    const ok = downloadExcelWorkbook(
      sheets,
      `excepcion_mora_analisis_${fechaDesde}_${fechaHasta}.xlsx`
    );
    showToast(ok ? 'Excel descargado' : 'No hay datos para exportar');
  };

  const exportReemplazosExcel = () => {
    if (!reemplazos.length) {
      showToast('No hay reemplazos para exportar');
      return;
    }
    const rows = reemplazos.map((r) => ({
      Rol: r.soy_titular ? 'Titular' : r.soy_reemplazo ? 'Reemplazo' : '',
      Titular: r.titular_nombre,
      'Mail titular': r.titular_mail,
      'Tipo titular': r.titular_tipo || '',
      'Grupo titular': r.titular_grupo || '',
      Reemplazo: r.reemplazo_nombre,
      'Mail reemplazo': r.reemplazo_mail,
      'Tipo reemplazo': r.reemplazo_tipo || '',
      'Grupo reemplazo': r.reemplazo_grupo || '',
      Desde: r.fecha_desde,
      Hasta: r.fecha_hasta,
      Estado: r.estado,
      Vigente: r.vigente ? 'Sí' : 'No',
    }));
    const ok = downloadExcelWorkbook(
      [{ name: 'Reemplazos', rows }],
      `excepcion_mora_reemplazos_${todayFileStamp()}.xlsx`
    );
    showToast(ok ? 'Excel descargado' : 'No se pudo exportar');
  };

  const exportDetalleModalExcel = () => {
    if (!selected || !detalle.length) {
      showToast('No hay documentos para exportar');
      return;
    }
    const rows = detalle.map((d) => ({
      Código: selected.codigo,
      Cliente: selected.cliente_nombre,
      RUT: formatRutCli(selected),
      Deudor: d.deudor_nombre || '',
      'RUT deudor': d.rut_deudor_cli || '',
      'N° Doc.': d.num_docto || '',
      'N° Op.': d.num_ope != null ? d.num_ope : '',
      Deuda: Number(d.deuda_neta || 0),
      Vencimiento: d.fec_vcto,
      'Días mora': d.dias_mora,
      'Mora TMC': Number(d.mora_tmc || 0),
      'Mora solicitada': Number(d.mora_solicitada || 0),
    }));
    const ok = downloadExcelWorkbook(
      [{ name: 'Documentos', rows }],
      `excepcion_mora_${selected.codigo}_documentos.xlsx`
    );
    showToast(ok ? 'Excel descargado' : 'No se pudo exportar');
  };

  const loadReporte = useCallback(async () => {
    if (!user) return;
    setLoadingReporte(true);
    try {
      const res = await moraReporteMargen(user, {
        fecha_desde: fechaDesde,
        fecha_hasta: fechaHasta,
      });
      setReporte(res);
    } catch (e) {
      console.error(e);
      showToast(e.message || 'Error al cargar reporte');
      setReporte(null);
    } finally {
      setLoadingReporte(false);
    }
  }, [user, fechaDesde, fechaHasta, showToast]);

  useEffect(() => {
    if (tab === 'reporte') loadReporte();
  }, [tab, loadReporte]);

  const loadReemplazos = useCallback(async () => {
    if (!user || !puedeGestionarReemplazos) return;
    setLoadingReemplazos(true);
    try {
      const res = await moraReemplazosListar(user);
      setReemplazos(Array.isArray(res?.reemplazos) ? res.reemplazos : []);
      setCandidatos(Array.isArray(res?.candidatos) ? res.candidatos : []);
      setMeReemplazan(Array.isArray(res?.me_reemplazan) ? res.me_reemplazan : []);
    } catch (e) {
      console.error(e);
      showToast(e.message || 'Error al cargar reemplazos');
      setReemplazos([]);
      setCandidatos([]);
      setMeReemplazan([]);
    } finally {
      setLoadingReemplazos(false);
    }
  }, [user, puedeGestionarReemplazos, showToast]);

  useEffect(() => {
    if (tab === 'reemplazos') loadReemplazos();
  }, [tab, loadReemplazos]);

  const handleCrearReemplazo = async (e) => {
    e.preventDefault();
    if (!puedeCrearReemplazo || !repMail) return;
    setSavingReemplazo(true);
    try {
      const res = await moraReemplazosCrear(user, {
        reemplazo_mail: repMail,
        fecha_desde: repDesde,
        fecha_hasta: repHasta,
      });
      showToast(res?.mensaje || 'Reemplazo configurado');
      setRepMail('');
      loadReemplazos();
    } catch (err) {
      console.error(err);
      showToast(err.message || 'No se pudo crear el reemplazo');
    } finally {
      setSavingReemplazo(false);
    }
  };

  const handleCancelarReemplazo = async (id) => {
    const ok = window.confirm('¿Cancelar este reemplazo?');
    if (!ok) return;
    setSavingReemplazo(true);
    try {
      const res = await moraReemplazosCancelar(user, { id });
      showToast(res?.mensaje || 'Reemplazo cancelado');
      loadReemplazos();
    } catch (err) {
      console.error(err);
      showToast(err.message || 'No se pudo cancelar');
    } finally {
      setSavingReemplazo(false);
    }
  };

  const resumen = reporte?.resumen || {};
  const porEjecutivo = reporte?.por_ejecutivo || [];
  const porMes = reporte?.por_mes || [];
  const detalleReporte = reporte?.detalle || [];

  const pendientesCount = useMemo(
    () =>
      esAplicaciones
        ? filtroEstado === 'PENDIENTE'
          ? solicitudes.filter((s) => s.estado === 'PENDIENTE').length
          : solicitudes.filter(
              (s) =>
                s.estado === 'APROBADA' &&
                String(s.estado_cursado || 'PENDIENTE').toUpperCase() === 'PENDIENTE'
            ).length
        : solicitudes.filter((s) => s.estado === 'PENDIENTE').length,
    [solicitudes, esAplicaciones, filtroEstado]
  );

  const detalleGroups = useMemo(() => groupRowsByDeudor(detalle), [detalle]);

  const puedeAnularUi =
    puedeAnular ||
    ['Gerencia', 'Administrador'].includes(String(user?.tipo_usuario || ''));

  const openPlazoModal = useCallback(async () => {
    setPlazoModalOpen(true);
    setLoadingPlazo(true);
    try {
      const res = await moraPlazoPagoObtener(user);
      const n = Number(res?.plazo_dias ?? 0);
      setPlazoDias(n);
      setPlazoDraft(String(n));
      setPlazoMeta({
        actualizado_por: res?.actualizado_por || null,
        actualizado_en: res?.actualizado_en || null,
      });
    } catch (e) {
      console.error(e);
      showToast(e?.message || 'Error al cargar plazo');
    } finally {
      setLoadingPlazo(false);
    }
  }, [user, showToast]);

  const openPizarraModal = useCallback(async () => {
    setPizarraModalOpen(true);
    setLoadingPizarra(true);
    setPizarraFiltro('');
    try {
      const res = await moraPizarraPagos(user);
      setPizarraFilas(Array.isArray(res?.filas) ? res.filas : []);
      setPizarraFechaCarga(res?.fecha_carga || null);
      setPizarraTotal(Number(res?.total ?? 0));
    } catch (e) {
      console.error(e);
      setPizarraFilas([]);
      setPizarraFechaCarga(null);
      setPizarraTotal(0);
      showToast(e?.message || 'Error al cargar la pizarra');
    } finally {
      setLoadingPizarra(false);
    }
  }, [user, showToast]);

  const pizarraFiltrada = useMemo(() => {
    const q = String(pizarraFiltro || '')
      .trim()
      .toLowerCase();
    if (!q) return pizarraFilas;
    return pizarraFilas.filter((r) => {
      const blob = [
        r.numero_documento,
        r.td,
        r.rut_cliente,
        r.dv_cliente,
        r.razon_social_cliente,
        r.rut_deudor,
        r.dv_deudor,
        r.razon_social_deudor,
        r.estado,
        r.ejecutivo,
        r.sucursal,
        r.gestionador,
        r.zona,
        r.observaciones,
      ]
        .map((v) => String(v ?? '').toLowerCase())
        .join(' ');
      return blob.includes(q);
    });
  }, [pizarraFilas, pizarraFiltro]);

  const savePlazo = useCallback(async () => {
    const n = Number(plazoDraft);
    if (!Number.isInteger(n) || n < 0) {
      showToast('Ingresa un entero >= 0');
      return;
    }
    setSavingPlazo(true);
    try {
      const res = await moraPlazoPagoGuardar(user, { plazo_dias: n });
      const saved = Number(res?.plazo_dias ?? n);
      setPlazoDias(saved);
      setPlazoDraft(String(saved));
      setPlazoMeta({
        actualizado_por: res?.actualizado_por || null,
        actualizado_en: res?.actualizado_en || null,
      });
      showToast('Plazo de pagos confirmados actualizado');
      setPlazoModalOpen(false);
    } catch (e) {
      console.error(e);
      showToast(e?.message || 'No se pudo guardar el plazo');
    } finally {
      setSavingPlazo(false);
    }
  }, [plazoDraft, user, showToast]);

  return (
    <div className="mora-app exc-page">
      <header className="mora-page-header">
        <div>
          <h1>
            {esAplicaciones
              ? 'Cursado de Excepciones de Mora'
              : 'Solicitudes de Excepción de Mora'}
          </h1>
          <p>
            {esAplicaciones
              ? 'Cola de cursado: las «TMC» van primero. También puedes ver pedidos aún pendientes de aprobación (solo consulta; el cursado se habilita cuando queden aprobadas). En «Preacordadas» aplicas la tasa de clientes con política vigente.'
              : 'Revisa en un vistazo el impacto de margen y decide aprobar o rechazar. El módulo Análisis de Mora resume el impacto de tasas especiales aprobadas. En Reemplazos puedes dejar a otro Líder o a Gerencia autorizados a resolver por un período.'}
          </p>
        </div>
        <div className="exc-header-actions">
          {puedeVerPizarra && (
            <button type="button" className="mora-btn outline" onClick={openPizarraModal}>
              Pizarra de pagos
            </button>
          )}
          {puedeConfigPlazo && (
            <button type="button" className="mora-btn outline" onClick={openPlazoModal}>
              Plazo pagos confirmados
            </button>
          )}
        </div>
      </header>

      <div className="exc-tabs">
        <button
          type="button"
          className={tab === 'bandeja' ? 'active' : ''}
          onClick={() => setTab('bandeja')}
        >
          {esAplicaciones
            ? `Bandeja${pendientesCount ? ` (${pendientesCount})` : ''}`
            : `Bandeja ${
                filtroEstado === 'PENDIENTE' && pendientesCount
                  ? `(${pendientesCount})`
                  : ''
              }`}
        </button>
        {puedeVerPreacordadasPendientes && (
          <button
            type="button"
            className={tab === 'preacordadas' ? 'active' : ''}
            onClick={() => setTab('preacordadas')}
          >
            {`Preacordadas${
              preacordadasMeta.n_documentos
                ? ` (${preacordadasMeta.n_documentos})`
                : ''
            }`}
          </button>
        )}
        {!esAplicaciones && (
          <button
            type="button"
            className={tab === 'reporte' ? 'active' : ''}
            onClick={() => setTab('reporte')}
          >
            Análisis de Mora
          </button>
        )}
        {!esAplicaciones && puedeGestionarReemplazos && (
          <button
            type="button"
            className={tab === 'reemplazos' ? 'active' : ''}
            onClick={() => setTab('reemplazos')}
          >
            Reemplazos
          </button>
        )}
      </div>

      {tab === 'bandeja' && (
        <section className="mora-card exc-card">
          {reemplazosVigentesBandeja.length > 0 && (
            <div className="exc-reemplazo-banner">
              Estás reemplazando a{' '}
              <strong>
                {reemplazosVigentesBandeja
                  .map(
                    (r) =>
                      `${r.titular_nombre || r.titular_mail}${
                        r.titular_grupo ? ` (${r.titular_grupo})` : ''
                      }`
                  )
                  .join(', ')}
              </strong>
              . Puedes aprobar o rechazar en su nombre hasta el fin del período.
            </div>
          )}
          <div className="exc-toolbar">
            <div className="exc-filters">
              {esAplicaciones ? (
                <label>
                  Estado
                  <select
                    value={filtroEstado}
                    onChange={(e) => {
                      const next = e.target.value;
                      setFiltroEstado(next);
                      if (next === 'APROBADA' || next === '') {
                        setFiltroCursado(next === 'APROBADA' ? 'PENDIENTE' : '');
                      }
                    }}
                  >
                    <option value="PENDIENTE">Pendientes de aprobación</option>
                    <option value="APROBADA">Aprobadas</option>
                    <option value="">Pendientes y aprobadas</option>
                  </select>
                </label>
              ) : (
                <label>
                  Estado
                  <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
                    <option value="PENDIENTE">Pendientes</option>
                    <option value="APROBADA">Aprobadas</option>
                    <option value="RECHAZADA">Rechazadas</option>
                    <option value="ANULADA">Anuladas</option>
                    <option value="">Todas</option>
                  </select>
                </label>
              )}
              {(esAplicaciones ? filtroEstado !== 'PENDIENTE' : filtroEstado === 'APROBADA' || !filtroEstado) && (
                <label>
                  {esAplicaciones ? 'Mostrar' : 'Cursado factoring'}
                  <select
                    value={filtroCursado}
                    onChange={(e) => setFiltroCursado(e.target.value)}
                  >
                    <option value="PENDIENTE">
                      {esAplicaciones ? 'Pendientes de cursar' : 'Pendiente cursado'}
                    </option>
                    <option value="OK">OK</option>
                    <option value="NO">NO</option>
                    <option value="">{esAplicaciones ? 'Todas (incl. gestionadas)' : 'Todos'}</option>
                  </select>
                </label>
              )}
            </div>
            <div className="exc-reemplazo-actions">
              <button type="button" className="mora-btn outline" onClick={loadBandeja}>
                Actualizar
              </button>
              <button
                type="button"
                className="mora-btn outline"
                onClick={exportBandejaExcel}
                disabled={!solicitudes.length || loading}
              >
                Exportar Excel
              </button>
            </div>
          </div>

          {loading ? (
            <div className="mora-empty">Cargando bandeja…</div>
          ) : (
            <div className="mora-table-wrap">
              <table className="exc-table">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Origen</th>
                    <th>Cliente</th>
                    <th>{esAplicaciones ? 'Documentos' : 'Operación'}</th>
                    <th>Ejecutivo</th>
                    <th>Tasa pedida</th>
                    {!esAplicaciones && <th>Margen ↓</th>}
                    <th>Nivel</th>
                    <th>Aprobador</th>
                    <th>Estado</th>
                    <th>Cursado</th>
                    <th>Fecha</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {solicitudes.map((s) => (
                    <tr
                      key={s.codigo}
                      className={
                        s.estado === 'PENDIENTE' ||
                        (s.estado === 'APROBADA' &&
                          String(s.estado_cursado || 'PENDIENTE').toUpperCase() === 'PENDIENTE')
                          ? String(s.origen || '').toUpperCase() === 'TMC'
                            ? 'is-pending is-tmc-priority'
                            : 'is-pending'
                          : ''
                      }
                    >
                      <td>
                        <strong>{s.codigo}</strong>
                      </td>
                      <td>
                        <span className={`exc-badge ${origenBadgeClass(s.origen)}`}>
                          {origenLabel(s.origen)}
                        </span>
                      </td>
                      <td className="exc-left">
                        <div className="exc-cli">{s.cliente_nombre}</div>
                        <div className="mora-microcopy">{formatRutCli(s)}</div>
                      </td>
                      <td className="exc-left">
                        <div>{esAplicaciones ? documentosLabel(s) : operacionLabel(s)}</div>
                        {s.deudores_label ? (
                          <div className="mora-microcopy">{s.deudores_label}</div>
                        ) : null}
                      </td>
                      <td className="exc-left">{s.ejecutivo_nombre}</td>
                      <td className="mora-warning">{PCT.format(Number(s.tasa_mensual_solicitada))}%</td>
                      {!esAplicaciones && (
                        <td className="mora-warning">
                          <strong>{CLP.format(Number(s.margen_perdido || 0))}</strong>
                        </td>
                      )}
                      <td>{nivelLabel(s.nivel_aprobacion, s.origen)}</td>
                      <td className="exc-left">
                        <div>{aprobadorLabel(s)}</div>
                        {s.estado !== 'PENDIENTE' && s.nivel_aprobacion === 'LIDER_EQUIPO' && (
                          <div className="mora-microcopy">
                            Asignada a {nivelLabel(s.nivel_aprobacion)}
                            {s.ejecutivo_grupo ? ` · ${s.ejecutivo_grupo}` : ''}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={`exc-badge ${String(s.estado).toLowerCase()}`}>
                          {s.estado}
                        </span>
                      </td>
                      <td>
                        {s.estado === 'APROBADA' ? (
                          <span
                            className={`exc-badge ${
                              String(s.estado_cursado || 'PENDIENTE').toLowerCase() === 'ok'
                                ? 'aprobada'
                                : String(s.estado_cursado || '').toLowerCase() === 'no'
                                  ? 'rechazada'
                                  : 'pendiente'
                            }`}
                          >
                            {cursadoLabel(s.estado_cursado)}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>{formatDateTime(s.fecha_solicitud)}</td>
                      <td>
                        <button
                          type="button"
                          className="mora-link-btn"
                          onClick={() => openDetalle(s.codigo)}
                        >
                          Ver
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!solicitudes.length && (
                    <tr>
                      <td colSpan={esAplicaciones ? 12 : 13} className="mora-muted">
                        No hay solicitudes para este filtro
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {tab === 'preacordadas' && puedeVerPreacordadasPendientes && (
        <section className="mora-card exc-card">
          <div className="exc-toolbar">
            <div>
              <p className="mora-muted" style={{ margin: 0, maxWidth: 720 }}>
                Pagos confirmados con mora de clientes que tienen tasa preacordada vigente.
                Al aplicar se crea la solicitud autoaprobada y pasa a{' '}
                <strong>Pendientes de cursar</strong>
                {preacordadasMeta.plazo_dias != null
                  ? ` · ventana de pago: ${preacordadasMeta.plazo_dias} día(s)`
                  : ''}
                .
              </p>
            </div>
            <div className="exc-reemplazo-actions">
              <button
                type="button"
                className="mora-btn outline"
                onClick={loadPreacordadasPendientes}
                disabled={loadingPreacordadas}
              >
                Actualizar
              </button>
            </div>
          </div>

          {loadingPreacordadas ? (
            <div className="mora-empty">Cargando preacordadas pendientes…</div>
          ) : !preacordadasPendientes.length ? (
            <div className="mora-empty">
              No hay documentos con preacordada pendientes de aplicar en la ventana actual.
            </div>
          ) : (
            <div className="exc-preacordadas-cola">
              {preacordadasPendientes.map((item) => {
                const docs = Array.isArray(item.documentos) ? item.documentos : [];
                const docsOk = docs.filter((d) => d.tasa_preacordada != null);
                const groups = groupRowsByDeudor(docs);
                const applyClientKey = String(item.rut);
                const aplicandoCliente = aplicandoKey === applyClientKey;
                return (
                  <article key={item.rut_cli || item.rut} className="exc-preacordada-card">
                    <div className="exc-preacordada-card-head">
                      <div>
                        <div className="exc-cli">{item.cliente_nombre || '—'}</div>
                        <div className="mora-muted">
                          {item.rut_cli}
                          {item.ejecutivo ? ` · ${item.ejecutivo}` : ''}
                          {` · ${docs.length} doc${docs.length === 1 ? '' : 's'}`}
                          {groups.length
                            ? ` · ${groups.length} deudor${groups.length === 1 ? '' : 'es'}`
                            : ''}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="mora-btn"
                        disabled={Boolean(aplicandoKey) || !docsOk.length}
                        onClick={() =>
                          aplicarPreacordadaCliente(item, docs, {
                            applyKey: applyClientKey,
                            label: item.cliente_nombre || item.rut_cli,
                          })
                        }
                      >
                        {aplicandoCliente ? 'Aplicando…' : 'Aplicar cliente (todos)'}
                      </button>
                    </div>
                    {groups.map((g) => {
                      const gOk = g.rows.filter((d) => d.tasa_preacordada != null);
                      const applyGroupKey = `${item.rut}::${g.key}`;
                      const aplicandoGrupo = aplicandoKey === applyGroupKey;
                      return (
                        <div key={g.key} className="exc-deudor-block">
                          <div className="exc-preacordada-card-head">
                            <div>
                              <strong>{g.nombre}</strong>
                              {g.rut ? (
                                <span className="mora-muted"> · {g.rut}</span>
                              ) : null}
                              <span className="mora-muted">
                                {` · ${g.rows.length} doc${g.rows.length === 1 ? '' : 's'}`}
                              </span>
                            </div>
                            <button
                              type="button"
                              className="mora-btn outline"
                              disabled={Boolean(aplicandoKey) || !gOk.length}
                              onClick={() =>
                                aplicarPreacordadaCliente(item, g.rows, {
                                  applyKey: applyGroupKey,
                                  label: `${g.nombre}${g.rut ? ` (${g.rut})` : ''}`,
                                })
                              }
                            >
                              {aplicandoGrupo ? 'Aplicando…' : 'Aplicar este deudor'}
                            </button>
                          </div>
                          <div className="mora-table-wrap">
                            <table className="exc-table">
                              <thead>
                                <tr>
                                  <th>N° Doc.</th>
                                  <th>Saldo / monto</th>
                                  <th>Vencimiento</th>
                                  <th>Fecha pago</th>
                                  <th>Días mora</th>
                                  <th>Tasa preacordada</th>
                                  <th>Tramo</th>
                                </tr>
                              </thead>
                              <tbody>
                                {g.rows.map((d) => (
                                  <tr key={`${item.rut}-${g.key}-${d.num_docto}`}>
                                    <td>
                                      <strong>{d.num_docto || '—'}</strong>
                                    </td>
                                    <td>{CLP.format(Number(d.deuda_neta || 0))}</td>
                                    <td>{formatDate(d.fec_vcto)}</td>
                                    <td>{formatDate(d.fecha_pago)}</td>
                                    <td>{d.dias_mora ?? '—'}</td>
                                    <td>
                                      {d.tasa_preacordada != null ? (
                                        <span className="exc-badge pendiente">
                                          {PCT.format(Number(d.tasa_preacordada))}%
                                        </span>
                                      ) : (
                                        <span className="mora-muted" title={d.error_tasa || ''}>
                                          Sin tasa ({d.error_tasa || 'revisar tramo'})
                                        </span>
                                      )}
                                    </td>
                                    <td className="mora-muted">
                                      {d.etiqueta_tramo || d.tipo_tasa || '—'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })}
                  </article>
                );
              })}
            </div>
          )}
        </section>
      )}

      {tab === 'reporte' && (
        <section className="mora-card exc-card">
          <div className="exc-toolbar">
            <div className="exc-filters">
              <label>
                Desde
                <input
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                />
              </label>
              <label>
                Hasta
                <input
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                />
              </label>
            </div>
            <div className="exc-reemplazo-actions">
              <button type="button" className="mora-btn outline" onClick={loadReporte}>
                Calcular
              </button>
              <button
                type="button"
                className="mora-btn outline"
                onClick={exportReporteExcel}
                disabled={!reporte || loadingReporte}
              >
                Exportar Excel
              </button>
            </div>
          </div>

          {loadingReporte ? (
            <div className="mora-empty">Calculando reporte…</div>
          ) : (
            <>
              <div className="mora-kpis exc-report-kpis">
                <div className="mora-kpi exception">
                  <small>Impacto margen (aprobadas)</small>
                  <strong>{CLP.format(Number(resumen.total_margen_perdido || 0))}</strong>
                  <span>{Number(resumen.n_solicitudes || 0)} solicitudes</span>
                </div>
                <div className="mora-kpi policy">
                  <small>Mora TMC total</small>
                  <strong>{CLP.format(Number(resumen.total_mora_tmc || 0))}</strong>
                </div>
                <div className="mora-kpi">
                  <small>Mora especial total</small>
                  <strong>{CLP.format(Number(resumen.total_mora_solicitada || 0))}</strong>
                </div>
                <div className="mora-kpi">
                  <small>Deuda neta (saldo adeudado + mora)</small>
                  <strong>{CLP.format(Number(resumen.total_deuda_neta || 0))}</strong>
                </div>
              </div>

              <h3 className="exc-section-title">Por ejecutivo</h3>
              <div className="mora-table-wrap">
                <table className="exc-table">
                  <thead>
                    <tr>
                      <th>Ejecutivo</th>
                      <th>Grupo</th>
                      <th>Solicitudes</th>
                      <th>Margen perdido</th>
                      <th>Deuda neta (c/ mora)</th>
                      <th>Tasa promedio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {porEjecutivo.map((r, idx) => (
                      <tr key={`${r.ejecutivo_nombre}-${idx}`}>
                        <td className="exc-left">{r.ejecutivo_nombre}</td>
                        <td>{r.ejecutivo_grupo || '—'}</td>
                        <td>{r.n_solicitudes}</td>
                        <td className="mora-warning">
                          {CLP.format(Number(r.margen_perdido || 0))}
                        </td>
                        <td>{CLP.format(Number(r.deuda_neta || 0))}</td>
                        <td>{PCT.format(Number(r.tasa_promedio || 0))}%</td>
                      </tr>
                    ))}
                    {!porEjecutivo.length && (
                      <tr>
                        <td colSpan={6} className="mora-muted">
                          Sin aprobaciones en el período
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <h3 className="exc-section-title">Por mes</h3>
              <div className="mora-table-wrap">
                <table className="exc-table">
                  <thead>
                    <tr>
                      <th>Año</th>
                      <th>Mes</th>
                      <th>Solicitudes</th>
                      <th>Margen perdido</th>
                    </tr>
                  </thead>
                  <tbody>
                    {porMes.map((r) => (
                      <tr key={`${r.anio}-${r.mes}`}>
                        <td>{r.anio}</td>
                        <td>{r.mes}</td>
                        <td>{r.n_solicitudes}</td>
                        <td className="mora-warning">
                          {CLP.format(Number(r.margen_perdido || 0))}
                        </td>
                      </tr>
                    ))}
                    {!porMes.length && (
                      <tr>
                        <td colSpan={4} className="mora-muted">
                          Sin datos mensuales
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <h3 className="exc-section-title">Detalle de aprobaciones</h3>
              <div className="mora-table-wrap">
                <table className="exc-table">
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Cliente</th>
                      <th>Operación</th>
                      <th>Ejecutivo</th>
                      <th>Fecha</th>
                      <th>TMC</th>
                      <th>Tasa</th>
                      <th>Margen ↓</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalleReporte.map((r) => (
                      <tr key={r.codigo}>
                        <td>
                          <button
                            type="button"
                            className="mora-link-btn"
                            onClick={() => {
                              setTab('bandeja');
                              openDetalle(r.codigo);
                            }}
                          >
                            {r.codigo}
                          </button>
                        </td>
                        <td className="exc-left">{r.cliente_nombre}</td>
                        <td className="exc-left">{operacionLabel(r)}</td>
                        <td className="exc-left">{r.ejecutivo_nombre}</td>
                        <td>{formatDateTime(r.fecha_solicitud)}</td>
                        <td>{PCT_TMC.format(Number(r.tmc_mensual))}%</td>
                        <td className="mora-warning">
                          {PCT.format(Number(r.tasa_mensual_solicitada))}%
                        </td>
                        <td className="mora-warning">
                          {CLP.format(Number(r.margen_perdido || 0))}
                        </td>
                      </tr>
                    ))}
                    {!detalleReporte.length && (
                      <tr>
                        <td colSpan={8} className="mora-muted">
                          Sin detalle
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      )}

      {tab === 'reemplazos' && puedeGestionarReemplazos && (
        <section className="mora-card exc-card">
          <p className="exc-reemplazo-intro">
            Configura quién puede aprobar o rechazar solicitudes en tu nombre, y por cuánto
            tiempo. Puedes dejar como reemplazo a otro Líder de equipo o a Gerencia.
          </p>

          {meReemplazan.length > 0 && (
            <div className="exc-reemplazo-banner">
              Hoy estás actuando como reemplazo de{' '}
              <strong>
                {meReemplazan
                  .map(
                    (r) =>
                      `${r.titular_nombre || r.titular_mail} (${formatDate(
                        r.fecha_desde
                      )} → ${formatDate(r.fecha_hasta)})`
                  )
                  .join('; ')}
              </strong>
              .
            </div>
          )}

          {puedeCrearReemplazo && (
            <form className="exc-reemplazo-form" onSubmit={handleCrearReemplazo}>
              <h3 className="exc-section-title">Nuevo reemplazo</h3>
              <div className="exc-filters">
                <label>
                  Reemplazo
                  <select
                    value={repMail}
                    onChange={(e) => setRepMail(e.target.value)}
                    required
                  >
                    <option value="">Selecciona…</option>
                    {candidatos.map((c) => (
                      <option key={c.mail} value={c.mail}>
                        {c.nombre}
                        {c.tipo_usuario ? ` · ${c.tipo_usuario}` : ''}
                        {c.grupo ? ` · ${c.grupo}` : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Desde
                  <input
                    type="date"
                    value={repDesde}
                    onChange={(e) => setRepDesde(e.target.value)}
                    required
                  />
                </label>
                <label>
                  Hasta
                  <input
                    type="date"
                    value={repHasta}
                    onChange={(e) => setRepHasta(e.target.value)}
                    required
                  />
                </label>
              </div>
              <div className="exc-reemplazo-actions">
                <button
                  type="submit"
                  className="mora-btn primary"
                  disabled={savingReemplazo || !repMail}
                >
                  {savingReemplazo ? 'Guardando…' : 'Guardar reemplazo'}
                </button>
                <button
                  type="button"
                  className="mora-btn outline"
                  onClick={loadReemplazos}
                  disabled={loadingReemplazos}
                >
                  Actualizar
                </button>
                <button
                  type="button"
                  className="mora-btn outline"
                  onClick={exportReemplazosExcel}
                  disabled={!reemplazos.length || loadingReemplazos}
                >
                  Exportar Excel
                </button>
              </div>
            </form>
          )}

          {!puedeCrearReemplazo && (
            <div className="exc-reemplazo-actions" style={{ marginBottom: 14 }}>
              <button
                type="button"
                className="mora-btn outline"
                onClick={loadReemplazos}
                disabled={loadingReemplazos}
              >
                Actualizar
              </button>
              <button
                type="button"
                className="mora-btn outline"
                onClick={exportReemplazosExcel}
                disabled={!reemplazos.length || loadingReemplazos}
              >
                Exportar Excel
              </button>
            </div>
          )}

          {loadingReemplazos ? (
            <div className="mora-empty">Cargando reemplazos…</div>
          ) : (
            <>
              <h3 className="exc-section-title">Mis reemplazos (como titular)</h3>
              <div className="mora-table-wrap">
                <table className="exc-table">
                  <thead>
                    <tr>
                      <th>Reemplazo</th>
                      <th>Rol</th>
                      <th>Desde</th>
                      <th>Hasta</th>
                      <th>Estado</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {reemplazos
                      .filter((r) => r.soy_titular)
                      .map((r) => (
                        <tr key={r.id} className={r.vigente ? 'is-pending' : ''}>
                          <td className="exc-left">
                            <div className="exc-cli">{r.reemplazo_nombre}</div>
                            <div className="mora-microcopy">{r.reemplazo_mail}</div>
                          </td>
                          <td>
                            {r.reemplazo_tipo || '—'}
                            {r.reemplazo_grupo ? ` · ${r.reemplazo_grupo}` : ''}
                          </td>
                          <td>{formatDate(r.fecha_desde)}</td>
                          <td>{formatDate(r.fecha_hasta)}</td>
                          <td>
                            <span
                              className={`exc-badge ${
                                r.estado === 'ACTIVO'
                                  ? r.vigente
                                    ? 'aprobada'
                                    : 'pendiente'
                                  : 'rechazada'
                              }`}
                            >
                              {r.estado === 'CANCELADO'
                                ? 'CANCELADO'
                                : r.vigente
                                  ? 'VIGENTE'
                                  : 'PROGRAMADO'}
                            </span>
                          </td>
                          <td>
                            {r.estado === 'ACTIVO' && (
                              <button
                                type="button"
                                className="mora-link-btn"
                                disabled={savingReemplazo}
                                onClick={() => handleCancelarReemplazo(r.id)}
                              >
                                Cancelar
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    {!reemplazos.some((r) => r.soy_titular) && (
                      <tr>
                        <td colSpan={6} className="mora-muted">
                          Aún no has configurado reemplazos
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <h3 className="exc-section-title">Donde figuro como reemplazo</h3>
              <div className="mora-table-wrap">
                <table className="exc-table">
                  <thead>
                    <tr>
                      <th>Titular</th>
                      <th>Rol</th>
                      <th>Desde</th>
                      <th>Hasta</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reemplazos
                      .filter((r) => r.soy_reemplazo)
                      .map((r) => (
                        <tr key={`rep-${r.id}`} className={r.vigente ? 'is-pending' : ''}>
                          <td className="exc-left">
                            <div className="exc-cli">{r.titular_nombre}</div>
                            <div className="mora-microcopy">{r.titular_mail}</div>
                          </td>
                          <td>
                            {r.titular_tipo || '—'}
                            {r.titular_grupo ? ` · ${r.titular_grupo}` : ''}
                          </td>
                          <td>{formatDate(r.fecha_desde)}</td>
                          <td>{formatDate(r.fecha_hasta)}</td>
                          <td>
                            <span
                              className={`exc-badge ${
                                r.estado === 'ACTIVO'
                                  ? r.vigente
                                    ? 'aprobada'
                                    : 'pendiente'
                                  : 'rechazada'
                              }`}
                            >
                              {r.estado === 'CANCELADO'
                                ? 'CANCELADO'
                                : r.vigente
                                  ? 'VIGENTE'
                                  : 'PROGRAMADO'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    {!reemplazos.some((r) => r.soy_reemplazo) && (
                      <tr>
                        <td colSpan={5} className="mora-muted">
                          Nadie te ha dejado como reemplazo
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      )}

      {modalOpen && selected && (
        <div className="exc-modal-backdrop" role="presentation" onClick={closeModal}>
          <div
            className="exc-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="exc-modal-header">
              <div>
                <h2>{selected.codigo}</h2>
                <p>
                  {selected.cliente_nombre} · {formatRutCli(selected)}
                </p>
              </div>
              <button type="button" className="exc-close" onClick={closeModal} aria-label="Cerrar">
                ✕
              </button>
            </div>

            <div className="exc-summary-grid">
              <div>
                <small>Ejecutivo</small>
                <strong>{selected.ejecutivo_nombre}</strong>
              </div>
              <div>
                <small>Operación</small>
                <strong>{operacionLabel(detalle.length ? detalle : selected)}</strong>
              </div>
              <div>
                <small>Fecha pago est.</small>
                <strong>{formatDate(selected.fecha_pago_estimada)}</strong>
              </div>
              <div>
                <small>TMC / Solicitada</small>
                <strong>
                  {PCT_TMC.format(Number(selected.tmc_mensual))}% →{' '}
                  <span className="mora-warning">
                    {PCT.format(Number(selected.tasa_mensual_solicitada))}%
                  </span>
                </strong>
              </div>
              <div>
                <small>Origen</small>
                <strong>
                  <span className={`exc-badge ${origenBadgeClass(selected.origen)}`}>
                    {origenLabel(selected.origen)}
                  </span>
                </strong>
              </div>
              <div>
                <small>Nivel asignado</small>
                <strong>{nivelLabel(selected.nivel_aprobacion, selected.origen)}</strong>
              </div>
              <div>
                <small>Aprobador</small>
                <strong>{aprobadorLabel(selected)}</strong>
              </div>
              <div className="exc-highlight">
                <small>Disminución de margen</small>
                <strong className="mora-warning">
                  {CLP.format(Number(selected.margen_perdido || 0))}
                </strong>
              </div>
              <div>
                <small>Estado</small>
                <strong>
                  <span className={`exc-badge ${String(selected.estado).toLowerCase()}`}>
                    {selected.estado}
                  </span>
                </strong>
              </div>
            </div>

            <div className="exc-kpis-mini">
              <div>
                <span>Deuda neta (saldo adeudado + mora)</span>
                <strong>
                  {CLP.format(
                    Number(selected.deuda_neta_seleccionada || 0) +
                      Number(selected.mora_solicitada || 0)
                  )}
                </strong>
              </div>
              <div>
                <span>Saldo Adeudado</span>
                <strong>{CLP.format(Number(selected.deuda_neta_seleccionada || 0))}</strong>
              </div>
              <div>
                <span>Mora TMC</span>
                <strong className="mora-positive">{CLP.format(Number(selected.mora_tmc || 0))}</strong>
              </div>
              <div>
                <span>Mora especial</span>
                <strong className="mora-warning">
                  {CLP.format(Number(selected.mora_solicitada || 0))}
                </strong>
              </div>
            </div>

            <div className="exc-motivo">
              <small>Motivo</small>
              <p>{selected.motivo}</p>
            </div>

            {esAdmin && selected.estado === 'PENDIENTE' && (
              <div className="exc-admin-tools">
                <button
                  type="button"
                  className="mora-btn outline"
                  disabled={resolving}
                  onClick={handleReenviarAprobacion}
                >
                  {resolving ? 'Enviando…' : 'Reenviar correo de aprobación'}
                </button>
                <span className="mora-muted">
                  Solo Admin · reenvía al aprobador y copia a Gerencia
                </span>
              </div>
            )}

            <div className="exc-toolbar" style={{ marginBottom: 8 }}>
              <h3 className="exc-section-title" style={{ margin: 0 }}>
                Documentos
              </h3>
              <button
                type="button"
                className="mora-btn outline"
                onClick={exportDetalleModalExcel}
                disabled={!detalle.length}
              >
                Exportar Excel
              </button>
            </div>

            <div className="mora-table-wrap">
              <table className="exc-table">
                <thead>
                  <tr>
                    <th>N° Doc.</th>
                    <th>N° Op.</th>
                    <th>Deuda</th>
                    <th>Vencimiento</th>
                    <th>Días</th>
                    <th>Mora TMC</th>
                    <th>Mora sol.</th>
                  </tr>
                </thead>
                <tbody>
                  {detalleGroups.map((g) => (
                    <React.Fragment key={g.key}>
                      <tr className="mora-deudor-group">
                        <td colSpan={7}>
                          <strong>{g.nombre}</strong>
                          {g.rut ? <span className="mora-muted"> · {g.rut}</span> : null}
                          <span className="mora-muted">
                            {` · ${g.rows.length} doc${g.rows.length === 1 ? '' : 's'}`}
                          </span>
                        </td>
                      </tr>
                      {g.rows.map((d) => (
                        <tr key={`${d.id}-${d.num_docto}-${d.num_ope}`}>
                          <td>{d.num_docto || '—'}</td>
                          <td>{d.num_ope != null && d.num_ope !== '' ? d.num_ope : '—'}</td>
                          <td>{CLP.format(Number(d.deuda_neta || 0))}</td>
                          <td>{formatDate(d.fec_vcto)}</td>
                          <td>{d.dias_mora}</td>
                          <td>{CLP.format(Number(d.mora_tmc || 0))}</td>
                          <td>{CLP.format(Number(d.mora_solicitada || 0))}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  {!detalle.length && (
                    <tr>
                      <td colSpan={7} className="mora-muted">
                        Sin detalle de documentos
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {selected.estado === 'PENDIENTE' && !esAplicaciones ? (
              <div className="exc-resolve">
                <label>
                  Comentario (opcional)
                  <textarea
                    rows={2}
                    value={comentario}
                    onChange={(e) => setComentario(e.target.value)}
                    placeholder="Nota de resolución…"
                  />
                </label>
                <div className="exc-resolve-actions">
                  <button
                    type="button"
                    className="mora-btn danger"
                    disabled={resolving}
                    onClick={() => handleResolver('RECHAZADA')}
                  >
                    Rechazar
                  </button>
                  <button
                    type="button"
                    className="mora-btn success"
                    disabled={resolving}
                    onClick={() => handleResolver('APROBADA')}
                  >
                    Aprobar
                  </button>
                </div>
              </div>
            ) : (
              <div className="exc-resolved">
                {esAplicaciones && selected.estado === 'PENDIENTE' && (
                  <p className="mora-muted" style={{ margin: '0 0 8px' }}>
                    Aún pendiente de aprobación ({nivelLabel(selected.nivel_aprobacion, selected.origen)}
                    ). Solo consulta; el cursado se habilita cuando quede aprobada.
                  </p>
                )}
                {selected.estado !== 'PENDIENTE' && (
                  <>
                    Resuelta por {selected.resuelto_por_nombre || selected.resuelto_por_mail || '—'}{' '}
                    el {formatDateTime(selected.fecha_resolucion)}
                    {selected.comentario_resolucion ? (
                      <p>Comentario: {selected.comentario_resolucion}</p>
                    ) : null}
                  </>
                )}
                {selected.estado === 'APROBADA' && (
                  <p style={{ marginTop: 8 }}>
                    Cursado factoring:{' '}
                    <strong>{cursadoLabel(selected.estado_cursado)}</strong>
                    {selected.cursado_por_nombre || selected.cursado_por_mail
                      ? ` · ${selected.cursado_por_nombre || selected.cursado_por_mail}`
                      : ''}
                    {selected.fecha_cursado
                      ? ` · ${formatDateTime(selected.fecha_cursado)}`
                      : ''}
                    {selected.comentario_cursado ? (
                      <span> — {selected.comentario_cursado}</span>
                    ) : null}
                  </p>
                )}
                {selected.estado === 'APROBADA' && puedeCursar ? (
                  <div className="exc-resolve" style={{ marginTop: 16 }}>
                    <label>
                      Comentario de cursado (opcional)
                      <textarea
                        rows={2}
                        value={comentario}
                        onChange={(e) => setComentario(e.target.value)}
                        placeholder="Ej. tasa aplicada en factoring / no corresponde…"
                      />
                    </label>
                    <div className="exc-resolve-actions">
                      <button
                        type="button"
                        className="mora-btn danger"
                        disabled={resolving}
                        onClick={() => handleCursar('NO')}
                      >
                        NO
                      </button>
                      <button
                        type="button"
                        className="mora-btn success"
                        disabled={resolving}
                        onClick={() => handleCursar('OK')}
                      >
                        OK
                      </button>
                    </div>
                    <p className="mora-muted" style={{ marginTop: 8, fontSize: 12 }}>
                      Indica si el cambio de tasa quedó cursado en el sistema factoring.
                    </p>
                  </div>
                ) : null}
                {selected.estado === 'APROBADA' && puedeAnularUi && !esAplicaciones ? (
                  <div className="exc-resolve" style={{ marginTop: 16 }}>
                    <label>
                      Motivo de anulación (opcional)
                      <textarea
                        rows={2}
                        value={comentario}
                        onChange={(e) => setComentario(e.target.value)}
                        placeholder="Ej. limpieza de prueba / corrección…"
                      />
                    </label>
                    <div className="exc-resolve-actions">
                      <button
                        type="button"
                        className="mora-btn danger"
                        disabled={resolving}
                        onClick={handleAnular}
                      >
                        Anular aprobación
                      </button>
                    </div>
                    <p className="mora-muted" style={{ marginTop: 8, fontSize: 12 }}>
                      Libera los documentos para una nueva solicitud. Solo Gerencia y Administrador.
                    </p>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      )}

      {pizarraModalOpen && puedeVerPizarra && (
        <div
          className="exc-modal-backdrop"
          onClick={() => !loadingPizarra && setPizarraModalOpen(false)}
        >
          <div
            className="exc-modal exc-modal-wide"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="pizarra-pagos-title"
          >
            <div className="exc-modal-header">
              <div>
                <h2 id="pizarra-pagos-title">Pizarra de pagos vigente</h2>
                <p>
                  Carga más reciente
                  {pizarraFechaCarga ? (
                    <>
                      : <strong>{formatDateTime(pizarraFechaCarga)}</strong>
                    </>
                  ) : (
                    ' (sin datos)'
                  )}
                  {pizarraTotal ? ` · ${pizarraTotal} fila${pizarraTotal === 1 ? '' : 's'}` : ''}
                </p>
              </div>
              <button
                type="button"
                className="mora-link-btn"
                onClick={() => setPizarraModalOpen(false)}
              >
                Cerrar
              </button>
            </div>
            {loadingPizarra ? (
              <div className="mora-empty">Cargando pizarra…</div>
            ) : !pizarraFilas.length ? (
              <div className="mora-empty">No hay filas en la última carga.</div>
            ) : (
              <>
                <div className="exc-pizarra-toolbar">
                  <label>
                    Buscar
                    <input
                      type="search"
                      value={pizarraFiltro}
                      onChange={(e) => setPizarraFiltro(e.target.value)}
                      placeholder="RUT, cliente, documento, ejecutivo…"
                    />
                  </label>
                  <button
                    type="button"
                    className="mora-btn outline"
                    onClick={openPizarraModal}
                    disabled={loadingPizarra}
                  >
                    Actualizar
                  </button>
                </div>
                <div className="exc-table-wrap">
                  <table className="exc-table">
                    <thead>
                      <tr>
                        <th>Doc</th>
                        <th>TD</th>
                        <th>Cliente</th>
                        <th>Deudor</th>
                        <th>F. pago</th>
                        <th>F. vcto</th>
                        <th>Monto</th>
                        <th>Estado</th>
                        <th>Ejecutivo</th>
                        <th>Sucursal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pizarraFiltrada.map((r, idx) => {
                        const rutCli =
                          r.rut_cliente != null && r.dv_cliente
                            ? `${r.rut_cliente}-${String(r.dv_cliente).toUpperCase()}`
                            : r.rut_cliente || '—';
                        const rutDeu =
                          r.rut_deudor != null && r.dv_deudor
                            ? `${r.rut_deudor}-${String(r.dv_deudor).toUpperCase()}`
                            : r.rut_deudor || '—';
                        return (
                          <tr key={`${r.numero_documento}-${rutCli}-${idx}`}>
                            <td>{r.numero_documento || '—'}</td>
                            <td>{r.td || '—'}</td>
                            <td>
                              <div className="exc-cell-stack">
                                <span>{r.razon_social_cliente || '—'}</span>
                                <span className="mora-muted">{rutCli}</span>
                              </div>
                            </td>
                            <td>
                              <div className="exc-cell-stack">
                                <span>{r.razon_social_deudor || '—'}</span>
                                <span className="mora-muted">{rutDeu}</span>
                              </div>
                            </td>
                            <td>{formatDate(r.fecha_pago)}</td>
                            <td>{formatDate(r.fecha_vencimiento)}</td>
                            <td>
                              {r.monto != null ? CLP.format(Number(r.monto) || 0) : '—'}
                            </td>
                            <td>{r.estado || '—'}</td>
                            <td>{r.ejecutivo || '—'}</td>
                            <td>{r.sucursal || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {pizarraFiltro && (
                  <p className="mora-muted" style={{ marginTop: 10, fontSize: 12 }}>
                    Mostrando {pizarraFiltrada.length} de {pizarraTotal}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {plazoModalOpen && puedeConfigPlazo && (
        <div
          className="exc-modal-backdrop"
          onClick={() => !savingPlazo && setPlazoModalOpen(false)}
        >
          <div className="exc-modal tmp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="exc-modal-header">
              <div>
                <h2>Plazo pagos confirmados</h2>
                <p>
                  Días calendario después de <code>fecha_pago</code> en que se puede pedir tasa.
                  Con N = 0 solo el mismo día del pago.
                </p>
              </div>
              <button
                type="button"
                className="mora-link-btn"
                onClick={() => setPlazoModalOpen(false)}
                disabled={savingPlazo}
              >
                Cerrar
              </button>
            </div>
            {loadingPlazo ? (
              <div className="mora-empty">Cargando…</div>
            ) : (
              <>
                <label>
                  Plazo (días)
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={plazoDraft}
                    onChange={(e) => setPlazoDraft(e.target.value)}
                    disabled={savingPlazo}
                  />
                </label>
                <p className="tmp-field-hint">
                  Vigente: <strong>{plazoDias}</strong> día{plazoDias === 1 ? '' : 's'}. Elegible si{' '}
                  <code>fecha_pago ≤ hoy ≤ fecha_pago + N</code>.
                </p>
                {(plazoMeta.actualizado_por || plazoMeta.actualizado_en) && (
                  <p className="tmp-field-hint">
                    Última actualización
                    {plazoMeta.actualizado_por ? ` por ${plazoMeta.actualizado_por}` : ''}
                    {plazoMeta.actualizado_en ? ` · ${plazoMeta.actualizado_en}` : ''}
                  </p>
                )}
                <div className="exc-modal-footer">
                  <button
                    type="button"
                    className="mora-btn neutral"
                    onClick={() => setPlazoModalOpen(false)}
                    disabled={savingPlazo}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="mora-btn primary"
                    onClick={savePlazo}
                    disabled={savingPlazo || loadingPlazo}
                  >
                    {savingPlazo ? 'Guardando…' : 'Guardar'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className={`mora-toast${toast ? ' show' : ''}`} role="status">
        {toast}
      </div>
    </div>
  );
};

export default SolicitudesExcepcionMora;
